import { useCallback, useEffect, useState } from "react";
import { workRateApi } from "@/lib/api-client";
import { Activity, Loader2, RotateCcw, FileCheck2 } from "lucide-react";

const VENTURE_COLORS = {
  UMBRELLA: "bg-amber-400", CHECKMATE: "bg-emerald-400", "GIFTED-OS": "bg-sky-400",
  "BRAND-SIGNAL": "bg-rose-400", "WONDER-WORLD": "bg-violet-400",
};

export const WorkRateMonitor = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await workRateApi.get()); } catch { /* standby */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // real-time ROI growth — refresh every minute
    return () => clearInterval(t);
  }, [load]);

  const max = Math.max(1, ...(data?.ventures || []).map((v) => v.artifacts_24h));
  return (
    <section className="uc-card p-5" data-testid="work-rate-monitor">
      <div className="flex items-center gap-2 mb-3">
        <Activity size={13} className="text-emerald-400" />
        <span className="font-mono-uc text-[10px] uppercase tracking-widest text-white/60">
          Work-Rate Monitor · Real Artifacts / 24h
        </span>
        <button data-testid="work-rate-refresh" onClick={load} disabled={loading}
                className="ml-auto text-white/40 hover:text-emerald-400 transition-colors disabled:opacity-40"
                title="Refresh work rate">
          {loading ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[
          { k: "ARTIFACTS", v: data?.total_artifacts ?? 0, tid: "work-rate-artifacts", cls: "text-white/90" },
          { k: "ROI ADDED", v: `$${(data?.total_roi_added ?? 0).toFixed(2)}`, tid: "work-rate-roi", cls: "text-emerald-400" },
          { k: "POW STEPS", v: data?.proof_of_work_steps ?? 0, tid: "work-rate-pow", cls: "text-amber-400" },
        ].map((s) => (
          <div key={s.k} className="border border-white/10 bg-black px-2 py-1.5">
            <div className="font-mono-uc text-[8px] uppercase tracking-widest text-white/40">{s.k}</div>
            <div className={`font-serif-display text-lg ${s.cls}`} data-testid={s.tid}>{s.v}</div>
          </div>
        ))}
      </div>
      {!loading && !(data?.ventures || []).length && (
        <div className="font-mono-uc text-[10px] text-white/35" data-testid="work-rate-empty">
          No Real Artifacts sealed in the last 24h — authorize an RFQ to put the estate to work.
        </div>
      )}
      <div className="space-y-2" data-testid="work-rate-ventures">
        {(data?.ventures || []).map((v) => (
          <div key={v.venture} className="flex items-center gap-3" data-testid={`work-rate-${v.venture}`}>
            <span className="font-mono-uc text-[9px] uppercase tracking-widest text-white/60 w-24 truncate">{v.venture}</span>
            <div className="flex-1 h-1.5 bg-white/[0.06]">
              <div className={`h-full ${VENTURE_COLORS[v.venture] || "bg-white/30"}`}
                   style={{ width: `${Math.max(4, (v.artifacts_24h / max) * 100)}%` }} />
            </div>
            <span className="font-mono-uc text-[10px] text-white/70 w-8 text-right">{v.artifacts_24h}</span>
            <span className={`font-mono-uc text-[9px] w-16 text-right ${v.roi_added_24h > 0 ? "text-emerald-400" : "text-white/30"}`}>
              {v.roi_added_24h > 0 ? `+$${v.roi_added_24h.toFixed(2)}` : "—"}
            </span>
            {v.latest?.download && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}${v.latest.download}`} target="_blank" rel="noreferrer"
                 title={v.latest.clinical_name} className="text-white/30 hover:text-emerald-400 transition-colors">
                <FileCheck2 size={11} />
              </a>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default WorkRateMonitor;
